"use client";

/**
 * Renders nothing unless forceShowVideo is true. Hiding the modal visually
 * (opacity, translate, pointer-events) would leave it in the accessibility
 * tree, where screen readers and the staging test bot find controls no
 * sighted user can see (LEV-1687).
 */

import { useCallback } from "react";
import { X } from "lucide-react";

/**
 * Served from the public `lev-public-assets` Vercel Blob store (one copy for
 * every environment; the app only reads it). To replace the video, upload the
 * new MP4 in the Vercel dashboard and swap this URL. LEV-2553.
 */
const TUTORIAL_VIDEO_URL =
  "https://jdl4x6tgm7awfshv.public.blob.vercel-storage.com/GMT20261007-225548_Clip_T.A.%20McCann%20-%20PSL%27s%20Clip%2010_07_2026.mp4";
/** WebVTT captions for the video above; regenerate when the video changes. */
const TUTORIAL_CAPTIONS_URL =
  "https://jdl4x6tgm7awfshv.public.blob.vercel-storage.com/walkthrough.vtt";

export interface TutorialPopupProps {
  /** When true, immediately show video modal */
  forceShowVideo?: boolean;
  /** Called when video modal is closed */
  onClose?: () => void;
}

export function TutorialPopup({ forceShowVideo, onClose }: TutorialPopupProps) {
  const handleCloseVideo = useCallback(() => {
    onClose?.();
  }, [onClose]);

  if (!forceShowVideo) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Tutorial video"
      className="fixed inset-0 z-modal-overlay flex items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleCloseVideo();
      }}
    >
      <div className="relative w-full max-w-4xl px-4">
        <button
          onClick={handleCloseVideo}
          className="absolute -top-10 right-4 flex h-8 w-8 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
          aria-label="Close video"
        >
          <X className="h-5 w-5" />
        </button>
        <div
          className="relative overflow-hidden rounded-xl"
          style={{ paddingBottom: "56.25%" }}
        >
          {/* crossOrigin: a captions track from another origin is refused without it. */}
          <video
            src={TUTORIAL_VIDEO_URL}
            crossOrigin="anonymous"
            controls
            autoPlay
            playsInline
            className="absolute inset-0 h-full w-full bg-black"
            aria-label="Lev tutorial video"
          >
            <track
              kind="captions"
              src={TUTORIAL_CAPTIONS_URL}
              srcLang="en"
              label="English"
              default
            />
          </video>
        </div>
      </div>
    </div>
  );
}
